import angular from 'angular';
import '../../components/osom';
import '../../components/queue';
import '../../components/permission';
import '../../services/place';
import '../../services/organization';
import '../../services/line';
import '../../services/area';
import '../../services/user';
import '../../services/resource-group';
import '../../services/group';
import '../../services/auth';
import '../../services/errors';
import { filter, forEach, get, indexOf, remove, set, sortBy, includes, some, map, flatten, intersection } from 'lodash';

const module = angular.module('whyline.modals.user', [
  'whyline.components.osom',
  'whyline.components.queue',
  'whyline.components.permission',
  'whyline.services.place',
  'whyline.services.organization',
  'whyline.services.line',
  'whyline.services.area',
  'whyline.services.user',
  'whyline.services.resource-group',
  'whyline.services.auth',
  'whyline.services.errors',
  'whyline.services.group',
]);

const UserModalController = function (
  $uibModalInstance,
  $translate,
  NotificationService,
  UserService,
  AreaService,
  LineService,
  ErrorService,
  userId,
  roles,
  placeId,
  $timeout,
  AuthService,
  ResourceGroupService,
  PlaceService,
  OrganizationService,
  dialog,
  blockUI,
  SegmentService,
  GroupService,
) {
  'ngInject';

  // Initialization
  this.newPw = {
    newPassword: '',
    newConfirmPassword: '',
  };
  this.showChangePassword = false;
  this.currentUser = UserService.GetCurrent();
  this.placeId = placeId;
  this.globalUserPermission = '';
  this.alreadyShown = {};
  this.alreadyShownSegment = {};

  this.user = {
    profile: {},
    local: {},
    permissions: [],
  };
  this.roles = sortBy(roles, 'name');
  this.boxLabelRequired = PlaceService.GetCurrent().configuration.boxLabelRequired;
  this.useSegments = get(OrganizationService.GetCurrent(), 'configuration.queuer.useSegments', false);

  this.isExecutive = false;
  this.showIsExecutive = false; // property to hide the switch if it is executive until functionality is developed.
  this.executiveId = null;
  this.data = {
    segments: [
    ]
  }

  this.sameUser = this.currentUser.id === userId;

  this.blockSaveButton = this.sameUser;
  
  const init = () => {
    const userPromise = userId ? UserService.GetOneAsPromiseFromServer(userId) : Promise.resolve(this.user);

    return Promise.resolve(userPromise).then((user) => {
      $timeout(() => {
        user.placeInfo = user.placeInfo || {};

        user.placeInfo[placeId] = user.placeInfo[placeId] || {};

        const placeInfo = user.placeInfo[placeId];

        this.user = UserService.Copy(user);

        this.isExecutive = placeInfo.isExecutive;
        this.executiveId = placeInfo.executiveId;

        this.globalUserPermission = UserService.GetCurrentRoleFor(this.user);

        if (this.useSegments) {
          SegmentService.GetAllAsPromise().then((segments) => {
            $timeout(() => {
              this.data.segments = Object.values(segments).map((segment) => ({
                ...segment,
                checked: includes(placeInfo.segments, segment._id),
              }));
              generateAlreadyShownSegment();
            });
          }).catch(ErrorService.handler);;
        }

        generateAlreadyShown();
      });
    }).catch(ErrorService.handler);
  }

  const groupTypes = () => {
    const { permissions } = this.user;
    // filter lines or group
    const linesType = permissions.filter(
      (line) => line.resourceGroup.type === 'line' && line.placeId === this.placeId,
    );
    const groupsType = permissions.filter(
      (group) => group.resourceGroup.type === 'group' && group.placeId === this.placeId,
    );
    return { linesType, groupsType };
  };

  const choseLinesIncludedInAGroup = () => {

    const { linesType, groupsType } = groupTypes();

    // get lines in group
    const groupLines = groupsType.map((group) => {
      const id = group.resourceGroup.resources[0].resourceId || group.resourceGroup.resources[0]._id;
      return GroupService.GetOne(id);
    });

    // get Lines
    const lineOnly = linesType.map((line) => {
      const id = line.resourceGroup.resources[0].resourceId || line.resourceGroup.resources[0]._id;
      return LineService.GetOne(id);
    });

    const linesGroup = groupLines.map((group) => group.lines);
    const idsLinesInGroups = linesGroup.map((line) => line.map((lineId) => lineId._id));
    const idLinesInGroup = flatten(idsLinesInGroups);
    const idLines = lineOnly.map((line) => line._id);
    const include = intersection(idLinesInGroup, idLines);

    if (include.length) {
      return true;
    }
    return false;
  };

  let validate = () => {
    const firstName = this.user.profile.firstName;
    const lastName = this.user.profile.lastName;
    const externalId = this.user.profile.externalId;
    const email = this.user.local.email;
    const password = this.newPw.newPassword;
    const confirmPassword = this.newPw.newConfirmPassword;
    const emailPattern = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    if (!firstName) {
      NotificationService.Warning($translate.instant('mandatory_user_name'));
      return false;
    }

    if (firstName.length < 2 || firstName.length > 512) {
      NotificationService.Warning($translate.instant('user_firstname_length'));
      return false;
    }

    if (!lastName) {
      NotificationService.Warning(
        $translate.instant('mandatory_user_lastname'),
      );
      return false;
    }

    if (lastName.length < 2 || lastName.length > 512) {
      NotificationService.Warning($translate.instant('user_lastname_length'));
      return false;
    }

    if (!/^[ '-:A-Za-z\xC0-\xCF\xD1-\xD6\xD8-\xDD\xDF-\xE5\xE7-\xF6\xF8-\xFD\xFF\u0104-\u0107\u010C\u010D\u0116-\u0119\u012E\u012F\u0141-\u0144\u0152\u0160\u0161\u016A\u016B\u0172\u0173\u0178-\u017E]+$/.test(firstName)) {
      NotificationService.Warning($translate.instant('char_forbidden_name'));
      return false;
    }

    if (!/^[ '-:A-Za-z\xC0-\xCF\xD1-\xD6\xD8-\xDD\xDF-\xE5\xE7-\xF6\xF8-\xFD\xFF\u0104-\u0107\u010C\u010D\u0116-\u0119\u012E\u012F\u0141-\u0144\u0152\u0160\u0161\u016A\u016B\u0172\u0173\u0178-\u017E]+$/.test(lastName)) {
      NotificationService.Warning($translate.instant('char_forbidden_name'));
      return false;
    }

    const externalIdMin = 4;
    const externalIdMax = 100;

    if (externalId && (externalId.length < externalIdMin || externalId.length > externalIdMax)) {

      NotificationService.Warning(
        $translate
          .instant('field_length')
          .replace('{field}', $translate.instant('user_name'))
          .replace('{min}', externalIdMin)
          .replace('{max}', externalIdMax)
      );
      return false;
    }


    if (!email) {
      NotificationService.Warning($translate.instant('mandatory_user_mail'));
      return false;
    }

    if (email && !emailPattern.test(email)) {
      NotificationService.Warning($translate.instant('invalid_email_format'));
      return false;
    }

    if (!this.globalUserPermission) {
      NotificationService.Warning(
        'Debes seleccionar un permiso global para el usuario.',
      );
      return false;
    }

    if (password !== confirmPassword) {
      NotificationService.Warning($translate.instant('pw_must_match'));
      return false;
    }

    if (
      (password || confirmPassword) &
      (password.length < 5 || confirmPassword.length < 5)
    ) {
      NotificationService.Warning($translate.instant('pw_min_length'));
      return false;
    }

    if (this.isExecutive) {
      if (!some(this.data.segments, ['checked', true])) {
        NotificationService.Warning($translate.instant('empty_segments'));
        return false;
      }
      if ( !this.executiveId ){
        NotificationService.Warning($translate.instant('empty_execytiveId'));
        return false;
      }
      if (!/^[ '-:A-Za-z\xC0-\xCF\xD1-\xD6\xD8-\xDD\xDF-\xE5\xE7-\xF6\xF8-\xFD\xFF\u0104-\u0107\u010C\u010D\u0116-\u0119\u012E\u012F\u0141-\u0144\u0152\u0160\u0161\u016A\u016B\u0172\u0173\u0178-\u017E]+$/.test(this.executiveId)) {
        NotificationService.Warning($translate.instant('char_forbidden_id'));
        return false;
      }
    }

    if (this.globalUserPermission.name === 'operator' && this.user.permissions.length === 0) {
      NotificationService.Warning($translate.instant('mandatory_line_or_process_operator'));
      return false;
    }

    if (this.boxLabelRequired) {
      const msjValidations = this.user.permissions
        .filter(
          permission =>
            permission.boxLabelEnabled &&
            permission.boxLabelVisible &&
            (!permission.boxLabel || !permission.boxLabel.trim()),
        )
        .map(permission =>
          $translate
            .instant('{name}_box_label_required')
            .replace('{name}', permission.resourceGroup.name.toUpperCase()),
        );

      if (msjValidations && msjValidations.length > 0) {
        msjValidations.forEach(msj => NotificationService.Warning(msj));
        return false;
      }
    }

    if (choseLinesIncludedInAGroup()) {
      NotificationService.Warning($translate.instant('lines_belong_to_a_group'));
      return false;
    }

    return true;
  };

  this.save = () => {
    if (validate()) {
      if (this.newPw.newPassword) {
        set(
          this,
          'user.local.password',
          AuthService.Encrypt(this.newPw.newPassword),
        );
      } else {
        set(this, 'user.local.password', null);
      }
      this.checkPlacePermission().then(() => {
        const userBlockUI = blockUI.instances.get('user-block-ui');

        userBlockUI.start();

        this.user.placeInfo[placeId].isExecutive = this.isExecutive;
        this.user.placeInfo[placeId].executiveId = this.executiveId;
        this.user.placeInfo[placeId].segments = map(filter(this.data.segments, 'checked'), '_id');

        const userToSave = UserService.Copy(this.user);
        if (userId) {
          dialog
            .confirm($translate.instant('active_permisos_change'))
            .then(() => {
              this.user.permissions = this.depopulatePermissions(
                this.user.permissions,
              );
              if (this.globalUserPermission.name === 'operator' && this.user.permissions.length === 0) {
                NotificationService.Warning($translate.instant('mandatory_line_or_process_operator'));
                return false;
              } else {
                return UserService.Update(this.user)
                .then(() => {
                  NotificationService.Success(
                    $translate
                      .instant('user_{name}_update_succ')
                      .replace('{name}', this.user.local.email),
                  );
                  NotificationService.Warning($translate.instant('user_add_rows_or_processes_warning'));
                  $uibModalInstance.close('cancel');
                })
                .catch(err => {
                  ErrorService.handler(err);
                  this.user = UserService.Copy(userToSave);
               });
              }
            })
            .catch(() => {
              NotificationService.Information(
                $translate.instant('operation_cancelled'),
              );
              $uibModalInstance.close('cancel');
            })
            .finally(userBlockUI.stop);
        } else {
          this.user.permissions = this.depopulatePermissions(
            this.user.permissions,
          );
          this.user.profile.language = PlaceService.GetCurrent().language;
          UserService.Create(this.user)
            .then(() => {
              NotificationService.Success(
                `Usuario ${this.user.local.email} creado correctamente.`,
              );
              $uibModalInstance.close('cancel');
            })
            .catch((err) => {
              ErrorService.handler(err);
              this.user = UserService.Copy(userToSave);              
            })
            .finally(userBlockUI.stop);
        }
      });
    }
  };

  this.selectResourceGroup = resourceGroup => {
    this.user.permissions.push({
      placeId: this.placeId,
      resourceGroup,
    });
    generateAlreadyShown();
  };

  const removeIfExists = (permission, permissions) => {
    const index = indexOf(permissions, permission);
    permissions.splice(index, 1);
  };

  this.removePermission = permission => {
    removeIfExists(permission, this.user.permissions);
    generateAlreadyShown();
  };

  this.cancel = () => {
    $uibModalInstance.close('cancel');
  };

  this.depopulatePermissions = function(permissions) {
    const placePermissions = permissions.filter(
      (permission) => permission.placeId === this.placeId,
    );
    return placePermissions.map((permission) => ({
      placeId: permission.placeId,
      resourceGroup: permission.resourceGroup._id,
      role: permission.role ? permission.role._id : this.globalUserPermission._id,
      boxLabel: permission.boxLabelEnabled && permission.boxLabelVisible ? permission.boxLabel : '',
    }));
  };

  this.checkPlacePermission = () => {
    // If global permission is not operator
    if (this.placeId && this.globalUserPermission.name === 'Statistical') {
      this.user.permissions = this.user.permissions.filter(permission => {
        const isPlaceType = permission.resourceGroup.type === 'place';
        const isFromThisPlace =
          permission.resourceGroup.placeId === this.placeId;
        return !isFromThisPlace || (isFromThisPlace && isPlaceType);
      });
    }

    if (this.placeId && this.globalUserPermission.name !== 'operator') {
      let placePermissionFound = false;
      // Look over all user's permissions
      this.user.permissions.forEach(permission => {
        if (
          permission.resourceGroup.type === 'place' &&
          permission.resourceGroup.placeId === this.placeId
        ) {
          permission.role = this.globalUserPermission;
          placePermissionFound = true;
        }
      });

      if (!placePermissionFound) {
        return ResourceGroupService.GetAllAsPromise()
          .then(resources =>
            $timeout(() => {
              const PlaceResourceGroup = filter(resources, {
                type: 'place',
                placeId: this.placeId,
              });
              this.user.permissions.push({
                placeId: this.placeId,
                resourceGroup: PlaceResourceGroup[0],
                role: this.globalUserPermission,
                boxLabel: '',
              });
              return;
            }),
          )
          .catch(ErrorService.handler);
      } else {
        return Promise.resolve();
      }
    } else if (this.placeId && this.globalUserPermission.name === 'operator') {
      // Look over all user's permissions
      remove(
        this.user.permissions,
        permission =>
          permission.resourceGroup.type === 'place' &&
          permission.resourceGroup.placeId === this.placeId,
      );
      return Promise.resolve();
    } else {
      return Promise.resolve();
    }
  };

  const generateAlreadyShown = () => {
    this.alreadyShown = {};
    forEach(this.user.permissions, permission => {
      if (permission.resourceGroup) {
        this.alreadyShown[permission.resourceGroup._id] =
          permission.resourceGroup;
      }
    });
  };

  this.addSegment = (selection) =>{
    this.alreadyShown = {};
    forEach(this.data.segments, segment =>{
      if(segment._id === selection._id){
       segment.checked = true;
       }
    });
    generateAlreadyShownSegment()
  }

  this.removeSegment = (selection)=> {
    forEach(this.data.segments, segment =>{
      if(segment._id === selection._id){
       segment.checked = false;
       }
    });
    generateAlreadyShownSegment();
  }

  const generateAlreadyShownSegment = () =>{
    this.alreadyShownSegment = {};
    forEach(this.data.segments, segment =>{
      this.alreadyShownSegment = filter(this.data.segments, 'checked');
    });
  }

  init();
};

module.exports = module.controller('UserModalController', UserModalController);
